import React, {useState, useEffect} from 'react';
import Modal from '../../../../Presentation/components/ui/Modal';
import {AppText, AppTitle, AppSubtitle, AppSmallText, AppCardTitle, AppBodyText} from '../../../../Presentation/components/ui/Typography';
import Button from '../../../../Presentation/components/ui/Button';
import {DashboardUser} from '../../models/user.model';
import {Shield, User, Check, X} from 'lucide-react';
import {useRoles} from '../../hooks/useRoles';
import {useUserStore} from '../../store/userStore';
import {useAuth} from '../../../auth/hooks/useAuth';
import StandardizedErrorHandler from '../../../../Services/common/StandardizedErrorHandler';
import {useNotification} from '../../../../Services/useNotification';

interface RoleAssignmentModalProps {
  user: DashboardUser | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (userId: number, roleId: number) => Promise<void>;
  loading?: boolean;
}

export const RoleAssignmentModal: React.FC<RoleAssignmentModalProps> = ({
  user,
  isOpen,
  onClose,
  onSubmit,
  loading = false
}) => {
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use the roles hook to get available roles
  const {
    roles,
    loading: rolesLoading,
    fetchRoles
  } = useRoles();

  // Use user store for role assignment
  const {assignRole} = useUserStore();

  // Get current user for assigned_by field
  const {user: currentUser} = useAuth();

  // Notification hook for success messages
  const {success: showSuccess} = useNotification();

  useEffect(() => {
    if (isOpen) {
      // Load available roles from API
      fetchRoles().catch(error => {
        StandardizedErrorHandler.getInstance().handleError(error, 'RoleAssignmentModal.fetchRoles');
      });

      // Set current user's role as selected
      if (user && user.role) {
        setSelectedRoleId(user.role.id);
      }
    }
  }, [isOpen, user, fetchRoles]);

  const handleRoleChange = (roleId: number) => {
    setSelectedRoleId(roleId);
  };

  const handleSubmit = async () => {
    if (!user || !selectedRoleId) {return;}

    setIsSubmitting(true);

    try {
      // Call the backend API to assign the role
      await assignRole({
        userId: user.id,
        roleId: selectedRoleId,
        assignedBy: currentUser?.id || 1 // Use current user ID or default to admin
      });

      // Show success notification
      const selectedRole = roles.find(r => r.id === selectedRoleId);
      showSuccess(`تم تعيين الدور "${selectedRole?.displayName || selectedRole?.name || 'الدور'}" للمستخدم بنجاح`);

      // Call the parent component's onSubmit if provided
      if (onSubmit) {
        await onSubmit(user.id, selectedRoleId);
      }

      // Close the modal on success
      onClose();
    } catch (error) {
      // Handle error using StandardizedErrorHandler to show notification
      StandardizedErrorHandler.getInstance().handleError(error, 'RoleAssignmentModal.handleSubmit');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {return null;}

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="تعيين الدور"
      width="max-w-4xl"
    >
      <div className="space-y-6">
        {/* User Info Section */}
        <div className="bg-gray-50 rounded-lg p-5">
          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            <div className="w-14 h-14 bg-brand-100 rounded-full flex items-center justify-center">
              <User className="w-7 h-7 text-brand-600" />
            </div>
            <div className="flex-1">
              <AppCardTitle className="mb-1">{user.username}</AppCardTitle>
              <AppCardTitle className="text-gray-600">{user.fullName || user.email}</AppCardTitle>
              <AppSmallText className="text-gray-500 mt-1">
                الدور الحالي: {user.role?.name || 'غير محدد'}
              </AppSmallText>
            </div>
          </div>
        </div>

        {/* Role Selection */}
        <div>
          <div className="mb-5">
            <AppBodyText className="font-medium text-gray-700 mb-3">
              اختر الدور الجديد:
            </AppBodyText>
          </div>

          <div className="max-h-96 overflow-y-auto space-y-3">
            {rolesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto mb-4"></div>
                  <AppText className="text-gray-600">جاري تحميل الأدوار...</AppText>
                </div>
              </div>
            ) : roles.length === 0 ? (
              <div className="text-center py-8">
                <AppText className="text-gray-500">لا توجد أدوار متاحة</AppText>
              </div>
            ) : (
              roles.map((role) => (
                <div
                  key={role.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                    selectedRoleId === role.id
                      ? 'bg-brand-50 border-brand-300 shadow-sm'
                      : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                  onClick={() => handleRoleChange(role.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 rtl:space-x-reverse">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selectedRoleId === role.id
                          ? 'bg-brand-600 border-brand-600'
                          : 'border-gray-300'
                      }`}>
                        {selectedRoleId === role.id && (
                          <Check className="w-2 h-2 text-white" />
                        )}
                      </div>
                      <div>
                        <AppSubtitle className="text-gray-800 flex items-center">
                          <Shield className="w-5 h-5 mr-2" />
                          {role.displayName || role.name}
                        </AppSubtitle>
                        {role.description && (
                          <AppSmallText className="text-gray-600 mt-1">
                            {role.description}
                          </AppSmallText>
                        )}
                      </div>
                    </div>
                    {selectedRoleId === role.id && (
                      <div className="text-brand-600">
                        <Check className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Selected Role Info */}
        {selectedRoleId && (
          <div className="bg-brand-50 rounded-lg p-4">
            <AppSmallText className="text-brand-800">
              تم اختيار الدور: {roles.find(r => r.id === selectedRoleId)?.displayName || roles.find(r => r.id === selectedRoleId)?.name}
            </AppSmallText>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <Button
            variant="outline"
            size="large"
            onClick={onClose}
            disabled={loading}
          >
            إلغاء
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            disabled={isSubmitting || !selectedRoleId}
          >
            {isSubmitting ? 'جاري التعيين...' : 'تعيين الدور'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
